import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import AudioPlayer from "react-h5-audio-player";
import "react-h5-audio-player/lib/styles.css";
import SignupModal from "./SignupModal";
import { Translator } from "./translator";


const FuoriPreview = () => {
  const { tourID } = useParams();
  const navigate = useNavigate();
  const { translateText, lang } = Translator();

  const [tourData, setTourData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [ttsAudioUrl, setTtsAudioUrl] = useState(null);
  const hasRendered = useRef(false);

  const user = JSON.parse(localStorage.getItem("user") || "null");

  const baseText = {
    loading: "Caricamento...",
    notFound: "“Fuori Rotta” non trovato",
    includeLabel: "Include:",
    defaultIncludes: [
      "Galleria fotografica",
      "Posizioni e luoghi d’interesse",
      "Descrizioni storiche",
      "Video",
    ],
    addToCart: "Aggiungi al carrello",
    buyNow: "Acquista",
    mustLogin: "Ti invitiamo ad effettuare prima l'accesso.",
    onlyWithSub: "Disponibile solo con abbonamento",



    texterror1: "Impossibile salvare nel carrello.",
    texterror2: "Si è verificato un errore durante l'aggiunta al carrello.",

    texterror3: "Errore nella creazione del pagamento.",
    texterror4: "Errore durante la generazione del pagamento.",

  };

  const [translatedText, setTranslatedText] = useState(baseText);

  useEffect(() => {
    const translateAll = async () => {
      const entries = Object.entries(baseText);
      const translatedEntries = await Promise.all(
        entries.map(async ([k, v]) =>
          Array.isArray(v)
            ? [k, await Promise.all(v.map((s) => translateText(s)))]
            : [k, await translateText(v)]
        )
      );
      setTranslatedText(Object.fromEntries(translatedEntries));
    };
    translateAll();
  }, [lang]);

  // Fetch “Fuori Rotta”
  useEffect(() => {
    const fetchTour = async () => {
      try {
        setLoading(true);
        const res = await axios.get(
          `https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com/getFuorirotta`,
          { params: { nodeID: tourID, lang } }
        );

        if (typeof res.data === "string" && res.data === "0") {
          setTourData(null);
        } else {
          const { tour, root } = res.data || {};
          // Normalize fields to what we expect on UI
          const normalized = {
            id: root,
            title:
              tour.titelo_it || tour.titolo_it || tour.Name_it || "Teche Tour",
            subheading: tour.subheading_it || tour.Subheading_it || "",
            description: tour.desc_it || tour.Description_it || "",
            fullPrice: tour.FullPrice ?? null,
            image:
              tour.Image &&
              `https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com/media/${tour.Image}`,
            audio: tour.TourAudio_it
              ? `https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com/media/${tour.TourAudio_it}`
              : null,
            includes:
              Array.isArray(tour.include_it) && tour.include_it.length
                ? tour.include_it
                : [],
            area: tour.Area || "",
          };
          setTourData(normalized);
        }
      } catch (e) {
        console.error("Error fetching FUORIROTTA tour:", e);
        setTourData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchTour();
  }, [tourID, lang]);


  const fetchPollyAudio = async (text) => {
    try {
      setTtsAudioUrl(null);
      const res = await fetch(
        `https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com/speak?text=${encodeURIComponent(
          text
        )}&lang=${lang}`
      );
      const audioUrl = await res.text();
      setTtsAudioUrl(audioUrl || null);
    } catch (err) {
      setTtsAudioUrl(null);
    }
  };


  const formatPrice = (value, locale = navigator.language || "it-IT") =>
  new Intl.NumberFormat(locale, { style: "currency", currency: "EUR" })
    .format(Number.isFinite(+value) ? +value : 0);


  // If there is no pre-recorded audio, synthesize from description
  useEffect(() => {
    if (!tourData) return;
    if (tourData.audio) {
      setTtsAudioUrl(null);
      return;
    }
    const desc = tourData.description || "";
    if (!desc.trim()) {
      setTtsAudioUrl(null);
      return;
    }
    fetchPollyAudio(desc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, tourData?.description, tourData?.audio]);

  // Ensure TTS refreshes once after mount on language switch
  useEffect(() => {
    if (!hasRendered.current) {
      hasRendered.current = true;
      return;
    }
    if (tourData?.description && !tourData?.audio) {
      fetchPollyAudio(tourData.description);
    }
  }, [lang]);

  const handleAddToCart = async () => {
    if (!user || !user.email) {
      alert(translatedText.mustLogin);
      setShowSignupModal(true);
      return;
    }
    try {
      const payload = { email: user.email, tourID: parseInt(tourID, 10) };
      const res = await axios.post(
        "https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com/saveToCart",
        payload
      );
      if (res.data?.status === "success") {
        window.dispatchEvent(new Event("showCartPopup"));
      } else {
        alert(translatedText.texterror1);
      }
    } catch {
      alert(translatedText.texterror2);
    }
  };

  const handleBuyNow = async () => {
    if (!user || !user.email) {
      alert(translatedText.mustLogin);
      setShowSignupModal(true);
      return;
    }
    try {
      const amount = tourData.fullPrice;
      const res = await axios.post(
        "https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com/createPaymentIntent",
        { amount }
      );
      const clientSecret = res.data?.clientSecret;
      if (!clientSecret) {
        alert(translatedText.texterror3);
        return;
      }
      navigate("/checkout", {
        state: {
          clientSecret,
          tourData,
          email: user.email,
        },
      });
    } catch {
      alert(translatedText.texterror4);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-gray-800">
        {translatedText.loading}
      </div>
    );
  }

  if (!tourData) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-gray-800">
        {translatedText.notFound}
      </div>
    );
  }


  return (
    <>
      {showSignupModal && <SignupModal onClose={() => setShowSignupModal(false)} />}

      <div className="relative min-h-screen w-full overflow-visible">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(0,0,0,0.65), rgba(0,0,0,0.65))${
                tourData.image ? `, url(${tourData.image})` : ""
              }
            `,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        />

        <div className="relative z-0 max-w-5xl mx-auto px-4 py-8 text-white">
          {/* Title + Subheading */}
          <div className="mb-6 text-center">
            <h1 className="text-3xl md:text-5xl font-bold leading-tight">
              {tourData.title}
            </h1>
            {tourData.subheading && (
              <h3 className="text-lg md:text-2xl mt-2 opacity-95">
                {tourData.subheading}
              </h3>
            )}
          </div>


          {/* Area badge */}
          {tourData.area && (
            <div className="mt-8 text-lg flex justify-center opacity-90">
              📌 {tourData.area}
            </div>
          )}


          {/* Description */}
          {tourData.description && (
            <p className="mt-6 md:mt-8 leading-relaxed text-base md:text-lg">
              {tourData.description}
            </p>
          )}

          {/* Audio (original or TTS) */}
          {(tourData.audio || ttsAudioUrl) && (
            <div className="mx-auto w-full md:w-3/5 mt-8">
              <AudioPlayer
                src={tourData.audio || ttsAudioUrl}
                autoPlay={false}
                showSkipControls={false}
                showJumpControls={false}
                customAdditionalControls={[]}
                customVolumeControls={["volume"]}
                layout="horizontal-reverse"
                style={{
                  "--rhap_play-button-color": "#324B8E",
                  "--rhap_play-button": "#324B8E",
                }}
              />
              <style>
                {`
                  .rhap_play-pause-button { color: #324B8E !important; }
                  .rhap_play-pause-button svg { fill: #324B8E !important; }
                `}
              </style>
            </div>
          )}

          {/* Content row: Includes + Price/Actions */}
          <div className="flex justify-between items-center gap-8 flex-wrap mt-16 mb-6">
            {/* Left side: Include List */}
            <div>
              <h3 className="text-white mb-2">{translatedText.includeLabel}</h3>
                <ul className="list-disc pl-5 text-white leading-none">
                {tourData.includes && tourData.includes.length > 0
                  ? tourData.includes.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))
                  : translatedText.defaultIncludes.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
              </ul>
            </div>

            {/* Right side: Price + Buttons */}
            <div className="flex flex-col items-end ml-auto text-right min-h-[160px] justify-end gap-2">
              {(tourData.fullPrice != null || tourData.priceDiscounted != null) && (
                <div className="mb-3">
                  <span className="text-2xl text-white font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    🏷️{formatPrice(tourData.fullPrice)}
                  </span>
                </div>
              )}


  <button
    onClick={() => navigate('/choose?tab=abbonamento')}
    className="px-4 py-1 text-sm md:text-base bg-[#49B15D] border-[#49B15D] rounded-full transition duration-200"
  >
    {translatedText.onlyWithSub}
  </button>



              
            </div>
          </div>

          
        </div>
      </div>
    </>
  );
};

export default FuoriPreview;
